import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tradeApi } from '@lib/axios';
import { toast } from '@components/ui/toast/Toast';
import dayjs from 'dayjs';


export const fetchPackages = createAsyncThunk(
  'packages/fetchPackages',
  async (params, { rejectWithValue }) => {
    const res = await tradeApi.post('/v1/packages', params);
    if (res?.code === 200) return res.data?.packages ?? [];
    return rejectWithValue(res?.error ?? 'Failed to fetch packages');
  }
);

export const applyPromoCode = createAsyncThunk(
  'packages/applyPromoCode',
  async (params, { rejectWithValue }) => {
    const res = await tradeApi.post('/v1/promo-validate', params);
    if (res?.code === 200) return res.data;
    return rejectWithValue(res?.error ?? 'Invalid promo code');
  }
);

export const createOrderId = createAsyncThunk(
  'packages/createOrderId',
  async (params, { rejectWithValue }) => {
    const res = await tradeApi.post('/v1/subscription-order', params);
    if (res?.code === 200) return { ...res.data, packageId: params.packageId };
    return rejectWithValue(res?.error ?? 'Failed to create order');
  }
);

export const fetchSubscriptionStatus = createAsyncThunk(
  'packages/fetchSubscriptionStatus',
  async (params, { rejectWithValue }) => {
    const res = await tradeApi.get('/v1/subscription-status', { params });
    if (res?.code === 200) return res.data;
    return rejectWithValue(res?.error ?? 'Failed to fetch subscription status');
  }
);

export const startUserTrial = createAsyncThunk(
  'packages/startUserTrial',
  async (_, { rejectWithValue }) => {
    const res = await tradeApi.post('/v1/start-trial');
    if (res?.code === 200) {
      toast.success('Your free trial has been activated');
      return res.data;
    }
    return rejectWithValue(res?.error ?? 'Failed to start trial');
  }
);


const packagesSlice = createSlice({
  name: 'packages',
  initialState: {
    list: [],
    promoCode: null,
    orderId: null,
    subscriptionStatus: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearOrderId: (state) => { state.orderId = null; },
    clearPromoCode: (state) => { state.promoCode = null; },
  },
  extraReducers: (builder) => {
    builder
      // fetchPackages
      .addCase(fetchPackages.pending,   (state) => { state.loading = true;  state.error = null; })
      .addCase(fetchPackages.fulfilled, (state, { payload }) => { state.loading = false; state.list = payload; })
      .addCase(fetchPackages.rejected,  (state, { payload }) => { state.loading = false; state.error = payload; })

      // applyPromoCode
      .addCase(applyPromoCode.fulfilled, (state, { payload }) => { state.promoCode = payload; })
      .addCase(applyPromoCode.rejected,  (_, { payload }) => { toast.error(payload); })

      // createOrderId
      .addCase(createOrderId.fulfilled, (state, { payload }) => { state.orderId = payload; })
      .addCase(createOrderId.rejected,  (_, { payload }) => { toast.error(payload); })

      // fetchSubscriptionStatus
      .addCase(fetchSubscriptionStatus.fulfilled, (state, { payload }) => { state.subscriptionStatus = payload; })

      // startUserTrial
      .addCase(startUserTrial.rejected, (_, { payload }) => { toast.error(payload); });
  },
});

export const selectHasActiveSubscription = (state) => {
  const payments = state.packages.subscriptionStatus?.payments ?? [];
  return payments.some(
    (sub) =>
      sub.status === 'success' &&
      sub.productType === 'd-new' &&
      sub.packageEndTime &&
      dayjs(sub.packageEndTime).isAfter(dayjs())
  );
};

export const { clearOrderId, clearPromoCode } = packagesSlice.actions;
export default packagesSlice.reducer;